import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { enableWebPush } from "@/lib/fcm";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If we're returning from an Emergent Google Auth callback, let
    // AuthCallback exchange the session_id first — don't preemptively call
    // /auth/me here or it will 401 before the cookie is set.
    if (typeof window !== "undefined" &&
        window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("fs_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("fs_token"))
      .finally(() => setLoading(false));
  }, []);

  // Register the browser for FCM push notifications whenever we have an
  // authenticated user. Non-blocking, silent on failure, only runs if the
  // portal is open (we don't prompt marketing-site visitors).
  useEffect(() => {
    if (!user) return;
    if (typeof window === "undefined") return;
    if (!window.location.pathname.startsWith("/portal")) return;
    enableWebPush().catch(() => {});
  }, [user]);

  const handleAuth = (data) => {
    if (data?.token) localStorage.setItem("fs_token", data.token);
    setUser(data);
    return data;
  };

  const login = async (email, password, turnstileToken) => {
    const r = await api.post("/auth/login", { email, password, turnstile_token: turnstileToken });
    return handleAuth(r.data);
  };

  const register = async (payload) => {
    const r = await api.post("/auth/register", payload);
    return handleAuth(r.data);
  };

  const registerSupplier = async (payload) => {
    const r = await api.post("/suppliers/register", payload);
    return handleAuth(r.data);
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("fs_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, registerSupplier, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
